import { useRef, useState, type ChangeEvent } from 'react';

interface ImageUploadFieldProps {
  label: string;
  description?: string;
  imageUrl: string;
  isUnset: boolean;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  onReset: () => void;
  previewClassName?: string;
}

export default function ImageUploadField({
  label,
  description,
  imageUrl,
  isUnset,
  disabled,
  onUpload,
  onReset,
  previewClassName,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있어요.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : '업로드에 실패했어요. 다시 시도해주세요.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div className="relative bg-stone-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            className={`w-full object-cover object-top ${previewClassName || 'h-48'}`}
          />
        ) : (
          <div className={`w-full flex flex-col items-center justify-center gap-2 text-stone-400 ${previewClassName || 'h-48'}`}>
            <i className="ri-image-add-line text-3xl" />
            <span className="text-xs">아직 이미지가 없어요</span>
          </div>
        )}
        {isUnset && (
          <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-stone-900/70 text-white">
            미설정
          </span>
        )}
      </div>

      <div className="p-5">
        <h3 className="text-sm font-semibold text-stone-900">{label}</h3>
        {description && <p className="text-xs text-stone-500 mt-1 leading-relaxed">{description}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <i className={`${uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'}`} />
            {uploading ? '업로드 중...' : '이미지 업로드'}
          </button>
          <button
            type="button"
            disabled={disabled || isUnset}
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-6-line" />
            이미지 제거
          </button>
        </div>

        {error && (
          <p className="text-xs text-red-600 mt-3">{error}</p>
        )}
      </div>
    </div>
  );
}